import React, { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  Building2,
  Clock,
  Globe,
  Briefcase,
  Award,
  Target,
  Users,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  AlertCircle,
  Flag,
  TrendingUp,
  Heart,
  Trophy,
  ClipboardList,
  Sunrise,
  Repeat,
  MessageCircle,
} from "lucide-react";
import OriginalResponsesDisplay from "./OriginalResponsesDisplay";

const InteractiveMetricCard = ({
  title,
  value,
  icon: Icon,
  index,
  isExpanded,
  onToggle,
  palette,
}) => {
  // Different background colors for visual variety
  const bgGradients = [
    "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)",
    "linear-gradient(135deg, rgba(var(--primary-color-rgb), 0.12) 0%, rgba(var(--primary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--secondary-color-rgb), 0.12) 0%, rgba(var(--secondary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--accent-color-rgb), 0.12) 0%, rgba(var(--accent-color-rgb), 0.06) 100%)",
  ];

  const gradient = bgGradients[index % bgGradients.length];

  // Split long text into chunks for better readability when expanded
  const formatExpandedText = (text) => {
    if (!text) return ["No information available"];

    // If text is shorter than 200 chars, don't split it
    if (text.length < 200) return [text];

    // Split by sentences for better paragraph breaks
    const sentences = text.split(/(?<=[.!?])\s+/);
    const paragraphs = [];
    let currentParagraph = "";

    sentences.forEach((sentence) => {
      if (currentParagraph.length + sentence.length > 300) {
        paragraphs.push(currentParagraph);
        currentParagraph = sentence;
      } else {
        currentParagraph += (currentParagraph ? " " : "") + sentence;
      }
    });

    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs;
  };

  // Display placeholder text if value is empty or undefined
  // Ensure displayValue is always a string to avoid React rendering errors
  const displayValue = (() => {
    if (value === undefined || value === null) {
      return "No information available";
    }
    if (typeof value === "object") {
      // If it's an object, extract value property or stringify
      if (typeof value.value !== "undefined") {
        return String(value.value);
      }
      if (value.description) {
        return value.description;
      }
      try {
        return JSON.stringify(value);
      } catch (e) {
        return "Complex object";
      }
    }
    return String(value);
  })();
  const paragraphs = formatExpandedText(displayValue);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      onClick={onToggle}
      className={`rounded-xl overflow-hidden backdrop-blur-sm border border-white/10 cursor-pointer transition-all duration-300 ${
        isExpanded ? "col-span-full" : ""
      }`}
      style={{
        background: gradient,
      }}
      whileHover={{
        scale: isExpanded ? 1 : 1.02,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
      }}
    >
      <motion.div
        className="p-6"
        initial={false}
        animate={{
          padding: isExpanded ? "1.5rem" : "1.5rem",
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <motion.div
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-white"
              style={{ backgroundColor: "var(--primary-color)" }}
              whileHover={{ scale: 1.1 }}
            >
              <Icon size={20} />
            </motion.div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-white/60"
          >
            <ArrowRight size={20} />
          </motion.div>
        </div>

        <AnimatePresence>
          {!isExpanded && (
            <motion.div
              className="text-white/70 line-clamp-3 text-sm"
              exit={{ opacity: 0, height: 0 }}
            >
              {displayValue}
            </motion.div>
          )}

          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 space-y-4"
            >
              {paragraphs.map((paragraph, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="text-white/80 leading-relaxed"
                >
                  {paragraph}
                </motion.p>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

const DynamicBrandBackgroundSection = ({
  background,
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  responseData,
  questionnaireData,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Enhanced animation controls
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 0.9, 0.7]);

  // Determine if this section should have the background on the left or right
  const isLeftAligned = sectionIndex % 2 === 0;

  // Cards per slide for mobile view
  const CARDS_PER_SLIDE = 3;

  // Set up CSS Variables for colors
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Helper function to convert hex to RGB for gradients
      const hexToRgb = (hex) => {
        // Remove # if present
        hex = hex.replace("#", "");

        // Convert 3-digit hex to 6-digits
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // Extract r, g, b values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `${r}, ${g}, ${b}`;
      };

      // Set RGB values for CSS variables
      try {
        document.documentElement.style.setProperty(
          "--primary-color-rgb",
          hexToRgb(palette.primary)
        );
        document.documentElement.style.setProperty(
          "--secondary-color-rgb",
          hexToRgb(palette.secondary)
        );
        document.documentElement.style.setProperty(
          "--accent-color-rgb",
          hexToRgb(palette.accent)
        );
      } catch (e) {
        console.error("Error setting RGB color values:", e);
      }
    }
  }, [palette]);

  // Define icons for different background information types
  const icons = {
    "Company History": Building2,
    "Brand History": Clock,
    "Industry Context": Briefcase,
    "Market Position": Globe,
    Competitors: Trophy,
    "Awards and Recognition": Award,
    "Target Market": Target,
    "Brand Description": ClipboardList,
    "Brand Origin": Sunrise,
    "Recent Shifts": Repeat,
    "Upcoming Shifts": TrendingUp,
    "Recent Excitement": Sparkles,
    "Upcoming Excitement": Flag,
    "Biggest Fans": Heart,
    Challenges: AlertCircle,
  };

  // Map background data to cards with appropriate icons
  const getBackgroundCards = () => {
    return Object.entries(background).map(([key, value], index) => {
      const Icon = icons[key] || Building2; // Default to Building2 icon if no match

      return {
        title: key,
        value: value,
        icon: Icon,
        index,
      };
    });
  };

  const backgroundCards = getBackgroundCards();

  // Group items into slides for mobile navigation
  const itemSlides = [];
  for (let i = 0; i < backgroundCards.length; i += CARDS_PER_SLIDE) {
    itemSlides.push(backgroundCards.slice(i, i + CARDS_PER_SLIDE));
  }

  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev < itemSlides.length - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : itemSlides.length - 1));
  };

  return (
    <motion.section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-24 overflow-hidden"
      style={{
        opacity,
        backgroundColor: "rgba(5, 8, 15, 0.95)", // Much darker background
      }}
    >
      {/* Background image with transparency mask blending */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <motion.div
            className={`absolute inset-0 h-full bg-center bg-cover bg-no-repeat`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              opacity: bgOpacity,
              filter: "contrast(1.2) brightness(1.15)",
              mixBlendMode: "soft-light",
              width: "75%",
              left: isLeftAligned ? "0" : "auto",
              right: isLeftAligned ? "auto" : "0",
              maskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Content container with proper positioning */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 ${
          isLeftAligned ? "ml-auto" : "mr-auto"
        }`}
      >
        <motion.h2
          className="text-5xl md:text-7xl mb-16 font-bold text-white"
          style={{ textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)" }}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Brand Background
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/60 max-w-3xl mb-12 text-xl"
        >
          Essential context about the brand's history, industry, and overall
          positioning.
        </motion.p>

        {/* AI-Generated Narrative Section */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                style={{ backgroundColor: "var(--primary-color)" }}
                whileHover={{ scale: 1.1 }}
              >
                <MessageCircle size={24} />
              </motion.div>
              <h3 className="text-3xl font-bold text-white">
                Background Analysis
              </h3>
            </div>

            <OriginalResponsesDisplay
              sectionType="brandBackground"
              responseData={responseData}
              questionnaireData={questionnaireData}
              textColor="rgba(255, 255, 255, 0.9)"
              accentColor="var(--primary-color)"
            />
          </motion.div>
        )}

        {/* Main Content */}
        <div className="space-y-10">
          {/* Desktop View: Interactive Cards Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-min">
            {backgroundCards.map((card, index) => (
              <InteractiveMetricCard
                key={`${card.title}-${index}`}
                title={card.title}
                value={card.value}
                icon={card.icon}
                index={index}
                isExpanded={expandedCardIndex === index}
                onToggle={() => toggleCard(index)}
                palette={palette}
              />
            ))}
          </div>

          {/* Mobile View: Interactive Cards Carousel */}
          <div className="md:hidden relative">
            <div className="flex justify-between items-center mb-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronLeft
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>

              <div className="text-white/60 text-sm">
                {itemSlides.length > 0
                  ? `${activeSlide + 1} / ${itemSlides.length}`
                  : "0 / 0"}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={nextSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronRight
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {itemSlides.length > 0 ? (
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {itemSlides[activeSlide].map((card, index) => (
                    <InteractiveMetricCard
                      key={index}
                      title={card.title}
                      value={card.value}
                      icon={card.icon}
                      index={index + activeSlide * CARDS_PER_SLIDE}
                      isExpanded={
                        expandedCardIndex ===
                        index + activeSlide * CARDS_PER_SLIDE
                      }
                      onToggle={() =>
                        toggleCard(index + activeSlide * CARDS_PER_SLIDE)
                      }
                      palette={palette}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-8 text-center bg-white/5 rounded-lg border border-white/10"
                >
                  <p className="text-white/70">
                    No content is available to display.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination dots - only show if there are multiple slides */}
            {itemSlides.length > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {itemSlides.map((_, index) => (
                  <motion.button
                    key={index}
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        activeSlide === index
                          ? "var(--primary-color)"
                          : "rgba(255, 255, 255, 0.3)",
                    }}
                    onClick={() => setActiveSlide(index)}
                    whileHover={{ scale: 1.3 }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default DynamicBrandBackgroundSection;
